// frontend/src/components/plot/charts/AccumulationPhenologyChart.js
import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Brush, ReferenceLine } from 'recharts';
import { getLighterColor } from '../../../utils/colorUtils';
import CustomTooltip from './CustomTooltip';
import { getFilteredChartData, formatXAxisTick, getGrowthStageKey, computeStageTransitions } from './chartUtils';

export default function AccumulationPhenologyChart({ speciesGroup, chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  const filteredData = useMemo(() => getFilteredChartData(chartData, forecastVisible), [chartData, forecastVisible]);

  const degreeDaysMetric = speciesGroup.metrics.find((m) => m.type.includes('degree_days'));
  const degreeHoursMetric = speciesGroup.metrics.find((m) => m.type.includes('degree_hours'));

  const sampleMetricType = degreeDaysMetric?.type || degreeHoursMetric?.type || speciesGroup.metrics[0]?.type;
  const stageKey = useMemo(() => getGrowthStageKey(chartData, sampleMetricType), [chartData, sampleMetricType]);
  const stageTransitions = useMemo(() => computeStageTransitions(filteredData, stageKey), [filteredData, stageKey]);

  const currentStage = useMemo(() => {
    if (!filteredData || filteredData.length === 0 || !stageKey) return 'N/A';

    let validPoint = null;
    const now = Date.now();
    for (let i = filteredData.length - 1; i >= 0; i--) {
      const pt = filteredData[i];
      if (pt[stageKey] !== undefined && pt[stageKey] !== null) {
        if (pt.timestamp <= now || !validPoint) {
          validPoint = pt;
          if (pt.timestamp <= now) break;
        }
      }
    }
    if (!validPoint) return 'N/A';

    const rawVal = validPoint[stageKey];
    const val = typeof rawVal === 'object' ? String(rawVal.name || rawVal.label || rawVal.value || rawVal.id) : String(rawVal);
    return isNaN(Number(val)) ? val : `Stage ${val}`;
  }, [filteredData, stageKey]);

  if (!degreeDaysMetric && !degreeHoursMetric) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      {/*
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
        <h5 style={{ margin: 0, color: '#334155', textTransform: 'capitalize' }}>
          {speciesGroup.name}
        </h5>
        <span style={{ fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#334155', padding: '3px 10px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
          Current Growth Stage: {currentStage}
        </span>
      </div>
      */}

      <div style={{ width: '100%', height: '280px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <CartesianGrid strokeDasharray="2 2" stroke="#f1f5f9" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => formatXAxisTick(ts, localTimeEnabled)}
              minTickGap={30}
              tick={{ fontSize: 10 }}
            />
            <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 10 }} domain={['auto', 'auto']} unit=" DD" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={['auto', 'auto']} unit=" DH" />

            <Tooltip content={<CustomTooltip localTimeEnabled={localTimeEnabled} />} />
            <Brush dataKey="time" height={24} stroke="#94a3b8" fill="#f8fafc" />

            {stageTransitions.map((tr, idx) => (
              <ReferenceLine
                key={`stage-line-${idx}-${tr.stage}`}
                yAxisId="left"
                x={tr.x}
                stroke="#475569"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
                label={{
                  value: tr.label,
                  position: 'insideTopRight',
                  fill: '#475569',
                  fontSize: 11,
                  fontWeight: 600,
                  offset: 5
                }}
              />
            ))}

            {visibleDevices.map((dev) => {
              const fcstColor = getLighterColor(dev.color, 40);
              return (
                <React.Fragment key={dev.id}>
                  {degreeDaysMetric && (
                    <>
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey={`${degreeDaysMetric.type}_${dev.id}_hist`}
                        name={`${degreeDaysMetric.label}\n(${dev.name})`}
                        stroke={dev.color}
                        fill={dev.color}
                        fillOpacity={0.12}
                        strokeWidth={1.5}
                        dot={{ r: 3, shape: 'circle' }}
                        connectNulls={true}
                      />
                      {forecastVisible && (
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey={`${degreeDaysMetric.type}_${dev.id}_fcst`}
                          name={`${degreeDaysMetric.label} forecast\n(${dev.name})`}
                          stroke={fcstColor}
                          fill={fcstColor}
                          fillOpacity={0.06}
                          strokeWidth={1.5}
                          dot={{ r: 3, shape: 'diamond' }}
                          connectNulls={true}
                        />
                      )}
                    </>
                  )}
                  {degreeHoursMetric && (
                    <>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey={`${degreeHoursMetric.type}_${dev.id}_hist`}
                        name={`${degreeHoursMetric.label}\n(${dev.name})`}
                        stroke={dev.color}
                        strokeWidth={2}
                        dot={{ r: 3, shape: 'circle' }}
                        connectNulls={true}
                      />
                      {forecastVisible && (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey={`${degreeHoursMetric.type}_${dev.id}_fcst`}
                          name={`${degreeHoursMetric.label} forecast\n(${dev.name})`}
                          stroke={fcstColor}
                          strokeWidth={2}
                          dot={{ r: 3, shape: 'diamond' }}
                          connectNulls={true}
                        />
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
